import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useIsSA } from '../../lib/userState';
import FloatingPartnerLogos from './FloatingPartnerLogos';

const IMG = 'https://static.prod-images.emergentagent.com/jobs/eaad4cf4-b013-4778-bbae-d982e539f07c/images';
const icon = {
  builders: `${IMG}/c53a92ee9f1b912a50672e6181a0ec70810285c13ba6bfc5d549d22965fffc94.jpeg`,
  suppliers: `${IMG}/730b7401b44928111bd6063a4f4b0fe3caf16a88a3b2e7d3f8bea8e0eff65e72.jpeg`,
  trades: `${IMG}/b1877c70653d9205876a344eca3f9b65da6257d19738734a53ab2334937bd265.jpeg`,
  pros: `${IMG}/6574a97a18715f9381c8031c195aea57c45b8691f4f39aae7cef70a554e855e7.jpeg`,
  partner: `${IMG}/b98ec4ca4cc15cec34ad7404fb6a3fbe8b017f5da9d1f2bda22b1f27e59a8460.jpeg`,
  community: `${IMG}/84c224b75867fe4730dafab9dbe98051f03e9a65bf4b06624a6e52e4293f8aa6.jpeg`,
  app: `${IMG}/0bcfb015a45b9f4d64d6683806449f4efab24de8690c0b816d1eb1435c16d715.jpeg`,
  oneOnOne: `${IMG}/8863b1b6067c9096603f6287aa835af445d4a6204958a12d9add38033cf904b1.jpeg`,
};

const noScrollbar = '[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]';

// Consumer-facing matcher tiles. Each routes to its own matcher/hub.
const findTiles = [
  { title: 'Builders', subtitle: 'New Home, Renovation or Home Improvement', cta: 'Shortlist a builder', to: '/workspace/tools/find-a-builder', img: icon.builders, testId: 'home-cta-tile-builders' },
  { title: 'Suppliers', subtitle: 'Building Materials, Products and More', cta: 'Shortlist a supplier', to: '/suppliers', img: icon.suppliers, testId: 'home-cta-tile-suppliers' },
  { title: 'Trades', subtitle: 'Find the right trade for your job', cta: 'Shortlist a trade', to: '/find/category/tradies', img: icon.trades, testId: 'home-cta-tile-trades' },
  { title: 'Professional Services', subtitle: 'Find someone that can help', cta: 'Shortlist a professional', to: '/find/category/professional-services', img: icon.pros, testId: 'home-cta-tile-pros' },
];

const CTASection = () => {
  const isSA = useIsSA();

  // Extra consumer connections. One-on-One Support is South Australia only.
  const consumerAux = [
    { title: 'Community Groups', cta: 'Join the community', to: '/community', img: icon.community, testId: 'home-cta-community-entry' },
    { title: 'Download the App', cta: 'Get it on iOS & Android', href: 'https://buildpilot.app', external: true, img: icon.app, testId: 'home-cta-app-download-entry' },
    ...(isSA ? [{ title: 'One-on-One Support', cta: 'SA only guidance', to: '/one-on-one-support', img: icon.oneOnOne, testId: 'home-cta-sa-one-on-one-entry' }] : []),
  ];

  const auxCols = isSA ? 'lg:grid-cols-3' : 'lg:grid-cols-2';

  return (
    <section id="community" className="py-10 sm:py-14 lg:py-16 bg-white">
      <div className="max-w-[1120px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative gradient-blue-bg rounded-3xl overflow-hidden shadow-xl border border-white/20 p-5 sm:p-8 lg:p-10">
          {/* Background glow */}
          <div className="absolute inset-0 opacity-20 pointer-events-none">
            <div className="absolute top-0 right-0 w-[340px] sm:w-[520px] h-[340px] sm:h-[520px] bg-white rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
            <div className="absolute bottom-0 left-0 w-[260px] sm:w-[420px] h-[260px] sm:h-[420px] bg-white rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
          </div>

          <div className="relative">
            {/* Header */}
            <div className="max-w-2xl mb-6 sm:mb-8">
              <h2 className="text-2xl sm:text-3xl lg:text-[2.4rem] font-bold text-white leading-[1.1] tracking-[-0.03em] mb-3">
                Connect, match and grow with BuildPilot
              </h2>
              <p className="text-sm sm:text-base text-white/80 leading-relaxed">
                Get matched with vetted builders, suppliers, trades and professionals, or partner with us to list your own business.
              </p>
            </div>

            {/* Split: consumers (left) vs businesses (right) */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6 items-stretch">
              {/* ---- Consumers ---- */}
              <div className="lg:col-span-8">
                <p className="text-white font-semibold text-sm sm:text-[15px] mb-3">For homeowners &amp; renovators</p>

                {/* Four find tiles (mobile: horizontal scroll) */}
                <div className={`flex sm:grid sm:grid-cols-2 gap-3 overflow-x-auto sm:overflow-visible snap-x snap-mandatory pb-3 sm:pb-0 -mx-1 px-1 ${noScrollbar}`}>
                  {findTiles.map((tile) => (
                    <Link
                      key={tile.title}
                      to={tile.to}
                      className="group shrink-0 w-[62%] sm:w-auto snap-start bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/20 hover:bg-white/20 hover:border-white/40 transition-all duration-300 flex flex-col"
                      data-testid={tile.testId}
                    >
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-white shadow-md mb-3 shrink-0 overflow-hidden">
                        <img src={tile.img} alt={tile.title} loading="lazy" className="w-full h-full object-contain p-1" />
                      </div>
                      <h3 className="text-white font-semibold text-[15px] sm:text-lg tracking-tight mb-1 leading-tight">{tile.title}</h3>
                      <p className="text-white/70 text-xs sm:text-sm leading-snug mb-3">{tile.subtitle}</p>
                      <span className="mt-auto inline-flex items-center gap-1.5 text-white text-sm font-medium group-hover:gap-2.5 transition-all">
                        {tile.cta}
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </Link>
                  ))}
                </div>

                {/* Consumer extras */}
                <div className={`mt-3 flex sm:grid sm:grid-cols-2 ${auxCols} gap-3 overflow-x-auto sm:overflow-visible snap-x snap-mandatory pb-1 -mx-1 px-1 ${noScrollbar}`}>
                  {consumerAux.map((item) => {
                    const cls = 'group shrink-0 w-[72%] sm:w-auto snap-start flex items-center gap-3 rounded-2xl p-3.5 border bg-white/10 border-white/20 hover:bg-white/20 hover:border-white/40 transition-all duration-300';
                    const inner = (
                      <>
                        <div className="w-11 h-11 rounded-xl bg-white shadow-md shrink-0 overflow-hidden">
                          <img src={item.img} alt={item.title} loading="lazy" className="w-full h-full object-contain p-0.5" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-white font-semibold text-sm tracking-tight leading-tight mb-0.5">{item.title}</h3>
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-white/80 group-hover:gap-2 group-hover:text-white transition-all">
                            {item.cta}
                            <ArrowRight className="w-3.5 h-3.5" />
                          </span>
                        </div>
                      </>
                    );
                    return item.external ? (
                      <a key={item.title} href={item.href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={item.testId}>
                        {inner}
                      </a>
                    ) : (
                      <Link key={item.title} to={item.to} className={cls} data-testid={item.testId}>
                        {inner}
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* ---- Businesses ---- */}
              <div className="lg:col-span-4">
                <p className="text-white font-semibold text-sm sm:text-[15px] mb-3">For businesses</p>
                <Link
                  to="/register-business"
                  className="group flex min-h-[220px] flex-col justify-between bg-[#febd59] rounded-2xl p-5 sm:p-6 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
                  data-testid="home-cta-partner-entry"
                >
                  <div>
                    <div className="w-14 h-14 rounded-2xl bg-white shadow-md overflow-hidden mb-4">
                      <img src={icon.partner} alt="Partner with BuildPilot" loading="lazy" className="w-full h-full object-contain p-1" />
                    </div>
                    <h3 className="text-[#1d1d1f] font-bold text-lg sm:text-xl leading-tight mb-1.5">List your business on BuildPilot</h3>
                    <p className="text-[#1d1d1f]/70 text-sm leading-relaxed">Reach thousands of homeowners actively planning builds and renovations across Australia.</p>
                  </div>
                  {/* Floating partner-logo constellation (desktop only) fills the card */}
                  <div className="hidden lg:block relative shrink-0 mt-3 mb-4 h-[250px]" data-testid="home-cta-partner-logos">
                    <FloatingPartnerLogos heightClass="absolute inset-0" count={9} minSize={42} sizeVar={22} refWidthPx={230} />
                  </div>
                  <span className="mt-5 inline-flex items-center gap-2 text-[#1d1d1f] font-semibold text-sm group-hover:gap-3 transition-all">
                    Become a partner
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
